import * as jwtDecodeModule from 'jwt-decode';
import { AUTH_CONFIG } from './auth0-variables';
import { getAuth0Client } from './auth0Client';

export const SESSION_EXPIRED_EVENT = 'gophr:session-expired';
export const SESSION_EXPIRED_REASON_DEFAULT = 'session_expired';
const ROLE_CLAIM_KEY = 'https://gophr.app/roles';
const SESSION_RENEWAL_BUFFER_MS = 60 * 1000;
// Hard ceiling so a contended cross-tab lock or stalled network can never hang boot/refresh.
const SILENT_RENEW_TIMEOUT_MS = 20 * 1000;
const SILENT_RENEW_TOKEN_TIMEOUT_SECONDS = 15;

let silentRenewPromise = null;

const resolveJwtDecode = () => {
    if (typeof jwtDecodeModule === 'function') {
        return jwtDecodeModule;
    }

    if (jwtDecodeModule && typeof jwtDecodeModule.jwtDecode === 'function') {
        return jwtDecodeModule.jwtDecode;
    }

    if (jwtDecodeModule && typeof jwtDecodeModule.default === 'function') {
        return jwtDecodeModule.default;
    }

    return null;
};

const jwtDecode = resolveJwtDecode();

export const isSilentRenewDisabled = () => false;

const getStoredValue = key => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return null;
    }

    return window.localStorage.getItem(key);
};

const parseExpiryValue = rawValue => {
    if (!rawValue) {
        return null;
    }

    try {
        const parsedValue = JSON.parse(rawValue);
        const numericValue = Number(parsedValue);
        return Number.isFinite(numericValue) ? numericValue : null;
    } catch (error) {
        const numericValue = Number(rawValue);
        return Number.isFinite(numericValue) ? numericValue : null;
    }
};

const normalizeAudienceList = audience => {
    if (Array.isArray(audience)) {
        return audience.filter(candidateAudience => typeof candidateAudience === 'string' && candidateAudience.length > 0);
    }

    return typeof audience === 'string' && audience.length > 0 ? [audience] : [];
};

const doesTokenMatchIssuer = tokenPayload => {
    if (!tokenPayload || typeof tokenPayload !== 'object') {
        return false;
    }

    if (!AUTH_CONFIG || !AUTH_CONFIG.issuer) {
        return true;
    }

    return tokenPayload.iss === AUTH_CONFIG.issuer;
};

const doesAccessTokenMatchAudience = tokenPayload => {
    if (!tokenPayload || typeof tokenPayload !== 'object') {
        return false;
    }

    if (!AUTH_CONFIG || !AUTH_CONFIG.audience) {
        return true;
    }

    return normalizeAudienceList(tokenPayload.aud).includes(AUTH_CONFIG.audience);
};

const isStoredSessionCompatible = () => {
    const accessPayload = decodeJwtPayload(getStoredAccessToken());
    const idPayload = decodeJwtPayload(getStoredIdToken());

    if (!accessPayload || !idPayload) {
        return false;
    }

    return doesTokenMatchIssuer(accessPayload) && doesTokenMatchIssuer(idPayload) && doesAccessTokenMatchAudience(accessPayload);
};

export const getStoredAccessToken = () => getStoredValue('access_token');

export const getStoredIdToken = () => getStoredValue('id_token');

export const getSessionExpiry = () => parseExpiryValue(getStoredValue('expires_at'));

export const hasStoredSessionTokens = () => Boolean(getStoredAccessToken() && getStoredIdToken());

export const getSessionTimeRemaining = () => {
    const expiresAt = getSessionExpiry();

    if (!expiresAt) {
        return null;
    }

    return expiresAt - Date.now();
};

export const isSessionExpired = () => {
    const expiresAt = getSessionExpiry();

    if (!expiresAt) {
        return true;
    }

    return Date.now() >= expiresAt;
};

export const hasActiveSession = () => Boolean(hasStoredSessionTokens() && !isSessionExpired() && isStoredSessionCompatible());

export const shouldRenewSessionSoon = (bufferMs = SESSION_RENEWAL_BUFFER_MS) => {
    const timeRemaining = getSessionTimeRemaining();

    if (timeRemaining === null) {
        return false;
    }

    return timeRemaining <= bufferMs;
};

export const clearStoredSession = () => {
    if (typeof window === 'undefined' || !window.localStorage) {
        return;
    }

    window.localStorage.removeItem('access_token');
    window.localStorage.removeItem('id_token');
    window.localStorage.removeItem('expires_at');
};

export const storeSession = authResult => {
    if (!authResult || !authResult.accessToken || !authResult.idToken || !authResult.expiresIn) {
        return null;
    }

    const expiresAt = authResult.expiresIn * 1000 + Date.now();

    if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('expires_at', JSON.stringify(expiresAt));
        window.localStorage.setItem('access_token', authResult.accessToken);
        window.localStorage.setItem('id_token', authResult.idToken);
    }

    return expiresAt;
};

export const renewSessionSilently = () => {
    if (silentRenewPromise) {
        return silentRenewPromise;
    }

    let renewalTimeoutId = null;
    const renewalTimeout = new Promise((_, reject) => {
        renewalTimeoutId = setTimeout(() => {
            reject(new Error('Silent session renewal timed out.'));
        }, SILENT_RENEW_TIMEOUT_MS);
    });

    const renewal = getAuth0Client()
        .getTokenSilently({ detailedResponse: true, timeoutInSeconds: SILENT_RENEW_TOKEN_TIMEOUT_SECONDS });

    silentRenewPromise = Promise.race([renewal, renewalTimeout])
        .then(tokenResult => {
            silentRenewPromise = null;
            // auth0-spa-js v2 does not always return id_token in silent renewal responses.
            // Fall back to the existing stored id_token so we don't invalidate the session.
            const idToken = tokenResult.id_token || getStoredIdToken();
            const expiresAt = storeSession({
                accessToken: tokenResult.access_token,
                idToken,
                expiresIn: tokenResult.expires_in,
            });
            if (!expiresAt) {
                throw new Error('Could not store renewed session.');
            }
            return { expiresAt };
        })
        .catch(error => {
            silentRenewPromise = null;
            throw error;
        })
        .finally(() => {
            if (renewalTimeoutId !== null) {
                clearTimeout(renewalTimeoutId);
                renewalTimeoutId = null;
            }
        });

    return silentRenewPromise;
};

export const decodeJwtPayload = token => {
    if (!token || typeof token !== 'string' || typeof jwtDecode !== 'function') {
        return null;
    }

    const normalizedToken = token.replace(/^Bearer\s+/i, '').trim();

    if (!normalizedToken || normalizedToken.split('.').length !== 3) {
        return null;
    }

    try {
        return jwtDecode(normalizedToken);
    } catch (error) {
        return null;
    }
};

export const getStoredRoles = () => {
    const accessPayload = decodeJwtPayload(getStoredAccessToken());
    const idPayload = decodeJwtPayload(getStoredIdToken());
    const roles = (accessPayload && accessPayload[ROLE_CLAIM_KEY]) || (idPayload && idPayload[ROLE_CLAIM_KEY]);

    return Array.isArray(roles) ? roles : null;
};

export const buildAuthorizationHeader = () => {
    const accessToken = getStoredAccessToken();
    return accessToken ? 'Bearer ' + accessToken : '';
};

export const notifySessionExpired = detail => {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
        return;
    }

    window.dispatchEvent(
        new CustomEvent(SESSION_EXPIRED_EVENT, {
            detail: detail || { reason: SESSION_EXPIRED_REASON_DEFAULT }
        })
    );
};